import React, { useContext, useEffect, useState } from "react";
import _ from "lodash";

import { useExperiencesStoryboards } from "src/query/experiences/storyboards";
import { StoryboardCard, PlaceholderStoryboardCard, UploadingStoryboardCard } from "../storyboardCard";
import { StoryboardsContext } from "../../context";
import { CreateStoryboardDialog } from "src/pages/experiences/components/createStoryboard/CreateStoryboardDialog";
import InfiniteScroll from "src/components/infiniteScroll";
import EmptyMessage from "src/pages/experiences/components/emptyMessage";
import { useWorkspaceSubscription } from "src/pages/experiences/hooks/useWorkspaceSubscription";
import { isStoryboardTypeEnabled } from "src/pages/experiences/constants/storyboard";

import styles from "./storyboardList.module.scss";

const storyboardNotFound = '/images/experiences/emptyStoryboard.svg';

interface StoryboardListProps {
  workspaceId: string;
  listId?: string | null;
  query?: string;
  type?: string | null;
  status?: string | null;
}

export const StoryboardList: React.FC<StoryboardListProps> = ({
  workspaceId,
  listId = null,
  query = "",
  type = null,
  status = null,
}) => {
  const { data, isLoading, loadMore, hasNextPage, isFetchingNextPage } =
    useExperiencesStoryboards(workspaceId, listId, query, type, status);

  const { uploads } = useContext(StoryboardsContext);
  const subscription = useWorkspaceSubscription();

  const [showCreate, setShowCreate] = useState(false);

  const storyboards = data?.pages.flatMap((page) => page.records) ?? [];

  // Restrict the list to content types enabled for this workspace subscription.
  const visibleStoryboards = storyboards.filter((storyboard) =>
    isStoryboardTypeEnabled(
      storyboard.page?.storyboardType ?? storyboard.type,
      subscription,
    ),
  );

  // A whole API page can be filtered out (e.g. all disabled types). When that
  // strands the user with no visible cards but more pages exist, pull the next
  // page so the list isn't left looking empty / stuck.
  useEffect(() => {
    if (!isFetchingNextPage && hasNextPage && visibleStoryboards.length === 0) {
      loadMore();
    }
  }, [isFetchingNextPage, hasNextPage, visibleStoryboards.length, loadMore]);

  if (isLoading) {
    return (
      <div className={styles.grid}>
        {_.times(4).map((i) => (
          <PlaceholderStoryboardCard key={i} />
        ))}
      </div>
    );
  }

  if (!visibleStoryboards.length && !uploads.length && !hasNextPage) {
    return (
      <>
        <EmptyMessage
          image={storyboardNotFound}
          action={{
            title: "Create Storyboard",
            icon: "paperflite-add",
            onClick: () => setShowCreate(true),
          }}
          message="Well, This is Awkward. Your search didn't yield any results, try with a different search term"
        />

        <CreateStoryboardDialog
          show={showCreate}
          onClose={() => setShowCreate(false)}
          workspaceId={workspaceId}
          listId={listId}
        />
      </>
    );
  }
console.log({ visibleStoryboards, uploads });
  return (
    <>
      <InfiniteScroll
        className={styles.grid}
        loading={isFetchingNextPage}
        hasMore={!!hasNextPage}
        loadMore={loadMore}
        loader={() =>
          _.times(4).map((i) => <PlaceholderStoryboardCard key={i} />)
        }
      >
        {uploads.map((upload) => (
          <UploadingStoryboardCard key={upload.id} upload={upload} />
        ))}
        {visibleStoryboards.map((storyboard) => (
          <StoryboardCard
            key={storyboard.id}
            storyboard={storyboard}
            workspaceId={workspaceId}
          />
        ))}
      </InfiniteScroll>

      <CreateStoryboardDialog
        show={showCreate}
        onClose={() => setShowCreate(false)}
        workspaceId={workspaceId}
        listId={listId}
      />
    </>
  );
};
